<template>
  <footer class="mt-24 bg-ink-900 text-ink-200 relative overflow-hidden">
    <!-- Decorative top gradient line -->
    <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent"></div>

    <div class="container-wide py-16">
      <div class="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-12">
        <div class="col-span-2 md:col-span-4">
          <div class="flex items-center gap-2 mb-4">
            <template v-if="footerLogo">
              <img :src="footerLogo" :alt="footerBrandName" class="h-8 w-auto object-contain rounded" />
            </template>
            <template v-else>
              <!-- favicon-32x32.png, not leadranks-logo.png -- see NavBar.vue's
                   matching comment (PageSpeed 2026-09-09: same icon, same fix). -->
              <!-- alt="" -- see NavBar.vue's matching comment; the adjacent
                   "LeadRanks" wordmark right after this icon already names
                   the brand. -->
              <img src="/favicon-32x32.png" alt="" class="w-8 h-8 rounded-lg object-cover" />
            </template>
            <span class="font-semibold text-white tracking-tight">
              <template v-if="whiteLabelStore.isActive">{{ footerBrandName }}</template>
              <template v-else>Lead<span class="text-brand-300">Ranks</span></template>
            </span>
          </div>
          <p v-if="footerTagline" class="text-sm text-ink-300 max-w-xs leading-relaxed">
            {{ footerTagline }}
          </p>
          <!-- Only renders icons that have a real destination. All three were
               href="#" -- a button that looks clickable and does nothing, which
               is worse than no button. Fabricating profile URLs was the other
               option and is the same mistake already rejected for the
               Organization sameAs JSON-LD (CLAUDE.md §9 #83). Fill in a real
               URL below and the icon appears on its own. -->
          <div v-if="activeSocials.length" class="flex items-center gap-3 mt-5">
            <a v-for="s in activeSocials" :key="s.label" :href="s.href"
              target="_blank" rel="noopener noreferrer"
              class="w-9 h-9 rounded-lg border border-ink-700 flex items-center justify-center text-ink-300 hover:text-white hover:border-ink-500 hover:bg-ink-800 transition-colors"
              :aria-label="s.label"
              v-html="s.svg"></a>
          </div>
        </div>

        <div class="col-span-1 md:col-span-2">
          <!-- h4 -> h3 applied 2026-09-19: Lighthouse's heading-order audit
               failed on the footer (h2 section -> h4 column heading skips
               h3). Class list is unchanged, and the shared base rule for
               h1-h6 is uniform, so this is a purely semantic fix with no
               visual change. -->
          <h3 class="text-2xs font-semibold uppercase tracking-[0.14em] text-ink-400 mb-4">Product</h3>
          <ul class="space-y-2.5">
            <li><router-link to="/features" class="text-sm text-ink-300 hover:text-white transition-colors">Features</router-link></li>
            <!-- Same gap, same fix, for the new SEO-specific deep-dive page
                 (2026-09-11) -- built alongside GEO Optimization's own
                 content refresh so both dedicated audits get a real footer
                 path, not just a sitemap entry. -->
            <li><router-link to="/seo-optimization" class="text-sm text-ink-300 hover:text-white transition-colors">SEO Optimization</router-link></li>
            <!-- Real gap fixed 2026-09-07: /geo-optimization existed as a
                 real, fully-built page (with its own citations, FAQ schema,
                 and BreadcrumbList JSON-LD) but had zero top-level nav or
                 footer entry -- its only human-facing links were nested one
                 click inside the Features mega-menu, a homepage hero
                 sentence, and the /features page body. Search engines could
                 still find it via sitemap.xml, but a visitor browsing the
                 footer (or anyone scanning for "what does this do") had no
                 path to it at all. -->
            <li><router-link to="/geo-optimization" class="text-sm text-ink-300 hover:text-white transition-colors">GEO Optimization</router-link></li>
            <!-- Free checker landing page (2026-09-18): the low-commitment
                 entry point for cold-email / social links. Same reasoning as
                 the /geo-optimization entry above -- a page with no footer
                 path is a page only search engines can find. -->
            <li><router-link to="/geo-checker" class="text-sm text-ink-300 hover:text-white transition-colors">Free GEO checker</router-link></li>
            <li><router-link to="/pricing"  class="text-sm text-ink-300 hover:text-white transition-colors">Pricing</router-link></li>
            <li><router-link to="/analyze"  class="text-sm text-ink-300 hover:text-white transition-colors">Start audit</router-link></li>
          </ul>
        </div>

        <div class="col-span-1 md:col-span-2">
          <h3 class="text-2xs font-semibold uppercase tracking-[0.14em] text-ink-400 mb-4">Resources</h3>
          <ul class="space-y-2.5">
            <li><router-link to="/learn"   class="text-sm text-ink-300 hover:text-white transition-colors">Learn</router-link></li>
            <li><router-link to="/about"   class="text-sm text-ink-300 hover:text-white transition-colors">About</router-link></li>
            <li><router-link to="/contact" class="text-sm text-ink-300 hover:text-white transition-colors">Contact</router-link></li>
          </ul>
        </div>

        <div v-if="footerCta" class="col-span-2 md:col-span-4">
          <h3 class="text-2xs font-semibold uppercase tracking-[0.14em] text-ink-400 mb-4">Start winning search</h3>
          <p class="text-sm text-ink-300 mb-4">{{ footerCta.tagline }}</p>
          <router-link :to="footerCta.to" class="btn-brand btn">
            {{ footerCta.label }} <Icon name="arrow-right" :size="14" />
          </router-link>
        </div>
      </div>

      <div class="mt-14 pt-6 border-t border-ink-800 flex flex-col md:flex-row items-center justify-between gap-4">
        <p class="text-xs text-ink-400">&copy; {{ year }} {{ footerBrandName }}. All rights reserved.</p>
        <div class="flex items-center gap-5 text-xs text-ink-400">
          <router-link to="/privacy" class="hover:text-white transition-colors">Privacy</router-link>
          <router-link to="/terms" class="hover:text-white transition-colors">Terms</router-link>
          <router-link to="/service-level-agreement" class="hover:text-white transition-colors">SLA</router-link>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '../ui/Icon.vue';
import { useWhiteLabelStore } from '../../stores/whiteLabelStore';
import { useUserStore } from '../../stores/userStore';

const whiteLabelStore = useWhiteLabelStore();
const userStore = useUserStore();

// Real UX bug fixed 2026-08-29 (reported live): the footer's "Start
// free" CTA was unconditional -- a paying Agency subscriber saw
// "Start free, no card required" on every page, which makes no sense
// for someone who already has a paid account. Same frontend-facing
// tier names AnalyzePage.vue/SettingsPage.vue's own TIER_DISPLAY_NAMES
// already use, mapped to the real DB tier (which is "scale"/"growth",
// Stripe's internal naming, not "agency"/"professional" -- see
// core/white_label.py's own fix comment for why).
const TOP_TIERS = new Set(['agency', 'scale', 'business', 'enterprise', 'admin']);
const footerCta = computed(() => {
  if (!userStore.isAuthenticated) {
    return { label: 'Start free', to: '/register', tagline: 'Audit your first page in 3–4 minutes — free, no card required.' };
  }
  const tier = (userStore.subscriptionTier || 'starter').toLowerCase();
  if (TOP_TIERS.has(tier)) return null; // already on the top self-serve tier -- nothing left to upsell
  return { label: 'Upgrade your plan', to: '/pricing', tagline: 'More audits, more lead credits, and white-label reports on Pro and Agency.' };
});

const footerBrandName = computed(() => {
  if (whiteLabelStore.isActive && whiteLabelStore.companyName) return whiteLabelStore.companyName;
  return 'LeadRanks';
});

// Hide the marketing tagline for white-label tenants — it's LeadRanks' own pitch, not theirs.
const footerTagline = computed(() => {
  if (whiteLabelStore.isActive) return '';
  return 'Precision SEO intelligence for teams that take organic growth seriously.';
});

const footerLogo = computed(() => {
  if (whiteLabelStore.isActive && whiteLabelStore.logoUrl) return whiteLabelStore.logoUrl;
  return null;
});

const year = new Date().getFullYear();
const socials = [
  { label: 'Twitter',  href: 'https://x.com/getLeadRanks', svg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/leadranks', svg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>' },
  // No GitHub account exists yet -- leave the href empty and the icon stays
  // hidden (activeSocials filters it out) rather than pointing somewhere dead.
  { label: 'GitHub',   href: '', svg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>' },
];

// A social icon is only worth rendering if it goes somewhere. Empty href means
// "no account yet", not "link to the current page" -- which is what '#' did.
const activeSocials = computed(() =>
  socials.filter((s) => s.href && s.href !== '#')
);
</script>
